import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Form, Button, Header, Dropdown, Grid, Loader, List, TextArea,
  TextAreaProps, CheckboxProps, DropdownProps, DropdownItemProps, Icon, Dimmer
} from 'semantic-ui-react';
import Swal from 'sweetalert2';
import type { SweetAlertOptions } from 'sweetalert2';
import { toast } from 'react-toastify';
import moment from 'moment';
import _ from 'lodash';
import axiosInstance from '../../helpers/axiosInstance';
import { utils } from '../../helpers/utils';
import { IdNameViewModel, ConstituentViewModel } from '../../types/ServerDTO';
import { Constituent, ConstituentNoteViewModel } from '../../types/ConstituentDTO';
import UserInfo from '../Common/SharedComponents/UserInfo';
import AppContext from '../../AppContext';
import ConstituentNote from './ConstituentNote';
import SwitchTheme from '../Common/SharedComponents/SwitchTheme';

type FormErrorFields = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
};

type NoteFormErrorFields = {
  notes: string;
}

let successCount = 0;

type OnAddConstituent = (constituent: ConstituentViewModel) => void;

export const ConstituentForm: React.FC<{ isModal?: boolean, onAddConstituent?: OnAddConstituent, handleModalCancel?: any, modalConstituentId?: any }> = ({ isModal, onAddConstituent, handleModalCancel, modalConstituentId }) => {
  const navigate = useNavigate();

  const { id } = useParams();

  let constituentId = 0;
  if (isModal === undefined) {
    constituentId = id ? parseInt(id) : 0;
  }
  if (modalConstituentId !== undefined && modalConstituentId > 0) {
    constituentId = modalConstituentId;
  }

  const { userInfo } = useContext(AppContext);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isNoteLoading, setIsNoteLoading] = useState<boolean>(false);
  const [isUpdateLoading, setIsUpdateLoading] = useState<boolean>(false);
  const [isNoteFocus, setIsNoteFocus] = useState<boolean>(false);
  const [formValues, setFormValues] = useState<Constituent>({} as Constituent);
  const [wards, setWards] = useState<IdNameViewModel[] | undefined>([]);
  const [neighborhoods, setNeighborhoods] = useState<IdNameViewModel[]>([]);
  const [constituentNoteResponse, setConstituentNoteResponse] = useState<ConstituentNoteViewModel>();
  const [formErrors, setFormErrors] = useState<FormErrorFields>({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: ''
  });
  const [noteFormErrors, setNoteFormErrors] = useState<NoteFormErrorFields>({
    notes: ''
  });

  useEffect(() => {
    if (isModal === undefined) {
      if (constituentId > 0) {
        document.title = 'Edit Constituent - Adjure';
      } else {
        document.title = 'Create Constituent - Adjure';
      }
    }
    fetchData();
  }, [isModal, constituentId]);

  useEffect(() => {
    if (userInfo && userInfo.OfficeId === "1" && constituentId === 0) {
      navigate('/');
    }
  }, [userInfo, constituentId, navigate]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      let apiUrl = `/Constituent/GetView`;
      if (constituentId > 0) {
        apiUrl = `/Constituent/GetConstituent/${constituentId}`;
        fetchNotes();
      }
      await axiosInstance.get(apiUrl).then(({ data, status }) => {
        if (status === 200) {
          let constituent = data as Constituent;
          // Setting default city & state
          if (constituentId === 0) {
            constituent.city = 'Minneapolis';
            constituent.stateId = 33; // MN state ID
          }
          if (constituent.wardId) {
            const wardIndex: any = _.findIndex(constituent.masterData?.wardNeighborhood, { id: constituent.wardId });
            setNeighborhoods((constituent.masterData?.wardNeighborhood[wardIndex] as any).neighborhoods);
            const tmpWards: any = [];
            tmpWards.push(constituent.masterData?.wardNeighborhood[wardIndex]);
            setWards(tmpWards);
          } else {
            const neighborhoods: any = [];
            constituent.masterData?.wardNeighborhood.map((ward: any) => {
              neighborhoods.push(...ward.neighborhoods);
              return true;
            });
            const tmpNeighborhoods: any = _.sortBy(_.uniqBy(neighborhoods, 'id'), 'name');
            setNeighborhoods(tmpNeighborhoods);
            setWards(constituent.masterData?.wardNeighborhood);
          }
          setFormValues(data);
        }
        successCount++;
        if (constituentId === 0 || successCount === 2) {
          setIsLoading(false);
        }
      })
        .catch((error) => {
          setIsLoading(false);
          console.log(error);
        });
    } catch (error: any) {
      setIsLoading(false);
      console.log(error);
    }
  }

  const fetchNotes = () => {
    axiosInstance.get(`/Constituent/GetConstituentNotes/${constituentId}`).then(({ data, status }) => {
      if (status === 200) {
        setConstituentNoteResponse(data);
      }
      successCount++;
      if (successCount === 3) {
        setIsLoading(false);
      }
      setIsNoteLoading(false);
    })
      .catch((error) => {
        setIsLoading(false);
        setIsNoteLoading(false);
        console.log(error);
      });
  }

  const handlePhoneNumberInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    const number = value.trim().replace(/[^0-9]/g, '');

    let formattedNumber = '';
    if (number.length < 4) {
      formattedNumber = number;
    } else if (number.length < 7) {
      formattedNumber = number.replace(/(\d{3})(\d{1})/, '$1-$2');
    } else if (number.length < 11) {
      formattedNumber = number.replace(/(\d{3})(\d{3})(\d{1})/, '$1-$2-$3');
    }
    setFormValues({ ...formValues, [name]: formattedNumber });
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormValues({ ...formValues, [name]: value });
  };

  const handleTextAreaChange = (event: React.ChangeEvent<HTMLTextAreaElement>, data: TextAreaProps) => {
    const { name, value } = event.target;
    setFormValues({ ...formValues, [name]: value });
  };

  const handleRadioButtonChange = (event: React.FormEvent<HTMLInputElement>, data: CheckboxProps, field: string) => {
    setFormValues({ ...formValues, [field]: data.value });
  };

  const handleDropdownChange = (event: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => {
    const { name, value } = data;
    const tmpValue = value === "" ? null : value;
    if (name === 'neighborhoodId' && formValues.wardId === null) {
      const tmpWards: any = [];
      masterData?.wardNeighborhood.map((ward: any) => {
        const wardIndex = _.findIndex(ward.neighborhoods, { id: tmpValue });
        if (wardIndex !== -1) {
          tmpWards.push(ward);
        }
        return true;
      });
      setWards(tmpWards);
      if (tmpWards.length === 1) {
        setFormValues({ ...formValues, [name]: tmpValue, wardId: tmpWards[0].id });
        const wardIndex: any = _.findIndex(masterData?.wardNeighborhood, { id: tmpWards[0].id });
        setNeighborhoods((masterData?.wardNeighborhood[wardIndex] as any).neighborhoods);
      } else {
        setFormValues({ ...formValues, [name]: tmpValue });
      }
    } else {
      if (name === 'wardId') {
        if (typeof tmpValue === 'number') {
          setFormValues({ ...formValues, [name]: tmpValue });
          const wardIndex: any = _.findIndex(masterData?.wardNeighborhood, { id: tmpValue });
          setNeighborhoods((masterData?.wardNeighborhood[wardIndex] as any).neighborhoods);
        } else {
          setFormValues({ ...formValues, [name]: tmpValue, neighborhoodId: null });
          const neighborhoods: any = [];
          masterData?.wardNeighborhood.map((ward: any) => {
            neighborhoods.push(...ward.neighborhoods);
            return true;
          });
          const tmpNeighborhoods: any = _.sortBy(_.uniqBy(neighborhoods, 'id'), 'name');
          setNeighborhoods(tmpNeighborhoods);
          setWards(masterData?.wardNeighborhood);
        }
      } else {
        setFormValues({ ...formValues, [name]: tmpValue });
      }
    }
  };

  const handleDropdownBlur = (event: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => {
    const { name, value } = data;
    validate(false, name, value);
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    validate(false, name, value);
  };

  const validate = async (validateAll: boolean, fieldName: string | null = null, value: any | null = null): Promise<FormErrorFields> => {
    return await new Promise((resolve, reject) => {
      let errors: FormErrorFields = { ...formErrors };
      try {
        if (validateAll) {
          errors = {
            firstName: !formValues?.firstName ? 'First name is required' : '',
            lastName: !formValues?.lastName ? 'Last name is required' : '',
            email: (formValues?.email && !utils.isValidEmail(formValues.email)) ? 'Please provide a valid email' : '',
            phoneNumber: formValues?.phoneNumber && formValues?.phoneNumber.length < 12 ? 'Please enter a valid phone number which has at least 10 digits' : '',
          };
        } else {
          switch (fieldName) {
            case 'firstName':
              errors.firstName = !value ? 'First name is required' : '';
              break;
            case 'lastName':
              errors.lastName = !value ? 'Last name is required' : '';
              break;
            case 'email':
              errors.email = (value && !utils.isValidEmail(value)) ? 'Please provide a valid email' : '';
              break;
            case 'phoneNumber':
              errors.phoneNumber = value && value.length < 12 ? 'Please enter a valid phone number which has at least 10 digits' : '';
              break;
            default:
              break;
          }
        }
        setFormErrors(errors);
        resolve(errors);
      } catch (error) {
        console.log(`Validation error`);
        reject(errors);
      }
    });
  }

  const handleSubmit = async () => {
    setIsUpdateLoading(true);
    const errors = await validate(true);
    const hasErrors = Object.values(errors).some((error) => error);
    if (!hasErrors) {
      // handle form submission 
      let payLoad = { ...formValues };
      payLoad.masterData = null;
      payLoad.firstName = payLoad.firstName.trim();
      payLoad.lastName = payLoad.lastName.trim();
      axiosInstance.post(`/Constituent/AddUpdateConstituent`, payLoad).then(({ data, status, statusText }) => {
        if (status === 200) {
          if (isModal === undefined) {
            if (payLoad.constituentId === 0) {
              toast.success('Constituent created successfully');
            } else {
              toast.success('Constituent updated successfully');
            }
            navigate('/constituents');
          } else {
            successCount = 0;
            let wardName = '';
            masterData?.wardNeighborhood.map((ward: any) => {
              const wardIndex = _.findIndex(ward.neighborhoods, { id: payLoad.wardId });
              if (wardIndex !== -1) {
                wardName = ward.name;
              }
              return true;
            });
            let neighborhoodName = '';
            let address = '';
            if (payLoad.address) {
              address += payLoad.address;
            }
            if (payLoad.city) {
              if (address) {
                address += ', ';
              }
              address += payLoad.city;
            }
            if (payLoad.stateId) {
              const stateIndex = _.findIndex(masterData?.state, { id: payLoad.stateId });
              if (address) {
                address += ', ';
              }
              address += masterData?.state[stateIndex].name;
            }
            if (payLoad.zipCode) {
              if (address) {
                address += ', ';
              }
              address += payLoad.zipCode;
            }
            const returnConstituent: ConstituentViewModel = {
              constituentId: data,
              constituentName: payLoad.firstName + ' ' + payLoad.lastName,
              ward: wardName,
              neighborhood: neighborhoodName,
              address: address,
              email: payLoad.email,
              phoneNumber: payLoad.phoneNumber,
              pronouns: payLoad.pronouns
            }
            onAddConstituent && onAddConstituent(returnConstituent);
          }
        }
        setIsUpdateLoading(false);
      });
    } else {
      setIsUpdateLoading(false);
    }
  };

  const validateNote = async (): Promise<NoteFormErrorFields> => {
    return await new Promise((resolve, reject) => {
      let errors: NoteFormErrorFields = { ...noteFormErrors };
      try {
        errors = {
          notes: !formValues.notes ? 'Required' : '',
        };
        setNoteFormErrors(errors);
        resolve(errors);
      } catch (error) {
        console.log(`Validation error`);
        reject(errors);
      }
    });
  }

  const handleNoteSubmit = async () => {
    setIsNoteLoading(true);
    const errors = await validateNote();
    const hasErrors = Object.values(errors).some((error) => error);
    if (!hasErrors) {
      // handle form submission 
      let payload = {
        constituentId: constituentId,
        note: formValues.notes
      };
      await axiosInstance.post('Constituent/AddUpdateConstituentNote', payload).then(({ data, status }) => {
        if (status === 200) {
          setIsNoteFocus(false);
          setFormValues({ ...formValues, notes: '' });
          fetchNotes();
        } else {
          setIsNoteLoading(false);
        }
      })
        .catch((error) => {
          setIsNoteLoading(false);
          console.log(error);
        });
    } else {
      setIsNoteLoading(false);
      console.log('No valid!');
    }
  };

  const handleDelete = () => {
    const apiUrl = `/Constituent/DeleteConstituent/${constituentId}`;
    const options: SweetAlertOptions = {
      title: 'Are you sure you want to delete?',
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
    };
    Swal.fire(options).then((result) => {
      if (result.isConfirmed) {
        axiosInstance.post(apiUrl, null).then(({ status }) => {
          if (status === 200) {
            toast.success('Constituent deleted successfully');
            navigate('/constituents');
          }
        });
      }
    });
  };

  const convertToDropDownProps = (MasterItems: Array<IdNameViewModel>) => {
    return (MasterItems || []).map((item: IdNameViewModel) => ({
      key: item.id,
      text: item.name,
      value: item?.id,
    } as DropdownItemProps));
  }

  const { masterData } = formValues;
  let withoutMasterData = { ...formValues };
  withoutMasterData.masterData = null;

  const buildBase64String = (constituentId: number, constituentName: string) => {
    let base64String = '';
    try {
      const filter = {
        constituent: {
          ids: [constituentId],
          isFocus: false,
          names: [constituentName]
        },
      }
      var jsonString = JSON.stringify(filter);

      // Encode JSON string as Base64
      base64String = btoa(jsonString);
    }
    catch (error) {
      console.log(error);
    }
    return base64String;
  }

  return (
    <section className={isModal ? '' : 'section'} style={{
      minHeight: isModal ? 'auto' : '100vh'
    }}>
      {isModal === undefined && (
        <div className="section__head">
          <div className="section__head-content">
            <Header as="h1">{constituentId === 0 ? 'Create' : 'Edit'} Constituent</Header>
          </div>
          <div className="section__actions">
            <UserInfo />
            <SwitchTheme />
          </div>
        </div>
      )}
      <div className='section__body'>
        <Dimmer.Dimmable as="div" dimmed={isLoading}>
          <Dimmer active={isLoading} inverted={true}>
            <Loader>Loading</Loader>
          </Dimmer>
          <Form className='form-custom'>
            <Dimmer.Dimmable as="div" dimmed={isUpdateLoading}>
              <Dimmer active={isUpdateLoading} inverted={true}>
                <Loader>Loading</Loader>
              </Dimmer>
              <Form.Group widths='equal'>
                <Form.Field>
                  <Form.Input
                    label="First name"
                    name="firstName"
                    value={formValues.firstName}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    error={!!formErrors?.firstName}
                    required
                    maxLength={100}
                  />
                  {formValues.firstName?.length === 100 && <div className="error-message" style={{ color: 'red' }}>Maximum character limit exceeded</div>}
                  {formErrors?.firstName && <div className="error-message" style={{ color: 'red' }}>{formErrors?.firstName}</div>}
                </Form.Field>
                <Form.Field>
                  <Form.Input
                    label="Last name"
                    name="lastName"
                    value={formValues.lastName}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    error={!!formErrors?.lastName}
                    required
                    maxLength={100}
                  />
                  {formValues.lastName?.length === 100 && <div className="error-message" style={{ color: 'red' }}>Maximum character limit exceeded</div>}
                  {formErrors?.lastName && <div className="error-message" style={{ color: 'red' }}>{formErrors?.lastName}</div>}
                </Form.Field>
                <Form.Field>
                  <Form.Input
                    label="Pronouns"
                    name="pronouns"
                    value={formValues.pronouns}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    maxLength={100}
                  />
                </Form.Field>
                {constituentId > 0 && formValues.caseCount > 0 && (
                  <Form.Field className='cases'>
                    <a href={`/dashboard?SearchFilters=${buildBase64String(constituentId, encodeURIComponent(`${formValues.firstName} ${formValues.lastName}`))}`} target="_blank" rel="noreferrer" onClick={() => {
                      localStorage.removeItem('dashboardFilter');
                    }}>{formValues.caseCount} Cases<Icon name='external' className='no-u ml-2' /></a>
                  </Form.Field>
                )}
              </Form.Group>
              <Form.Group widths='equal'>
                <Form.Field>
                  <Form.Input
                    label="Email"
                    name="email"
                    value={formValues.email}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    error={!!formErrors?.email}
                    maxLength={100}
                  />
                  {formValues.email?.length === 100 && <div className="error-message" style={{ color: 'red' }}>Maximum character limit exceeded</div>}
                  {formErrors?.email && <div className="error-message" style={{ color: 'red' }}>{formErrors?.email}</div>}
                </Form.Field>
              </Form.Group>
              <Form.Group widths='equal'>
                <Form.Field>
                  <Form.Input
                    label="Phone number"
                    name="phoneNumber"
                    value={formValues.phoneNumber}
                    onChange={handlePhoneNumberInputChange}
                    onBlur={handleBlur}
                    maxLength={12}
                  />
                  {formErrors?.phoneNumber && <div className="error-message" style={{ color: 'red' }}>{formErrors?.phoneNumber}</div>}
                </Form.Field>
                <Form.Field>
                  <label>Phone type</label>
                  <Form.Group inline>
                    {masterData?.phoneType.map(phoneType => {
                      return (
                        <Form.Radio
                          label={phoneType.name}
                          value={phoneType.id}
                          checked={formValues.phoneTypeId === phoneType.id}
                          onChange={(e, data) => handleRadioButtonChange(e, data, 'phoneTypeId')}
                          onBlur={handleBlur}
                          style={{
                            margin: '1rem 1rem 0rem 1rem',
                            fontSize: '1.6rem'
                          }}
                        />
                      )
                    })}
                  </Form.Group>
                </Form.Field>
                <Form.Field>
                  <div className='field'>
                    <label>Preferred contact</label>
                    <Dropdown
                      name="preferredContactId"
                      options={convertToDropDownProps(masterData?.preferredContact || [])}
                      fluid
                      selection
                      clearable
                      value={formValues.preferredContactId ? formValues.preferredContactId : ''}
                      onChange={handleDropdownChange}
                      onBlur={handleDropdownBlur}
                    />
                  </div>
                </Form.Field>
              </Form.Group>
              <Form.Group widths='equal'>
                <Form.Field>
                  <Form.Input
                    label="Address"
                    name="address"
                    value={formValues.address}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    maxLength={500}
                  />
                  {formValues.address?.length === 500 && <div className="error-message" style={{ color: 'red' }}>Maximum character limit exceeded</div>}
                </Form.Field>
              </Form.Group>
              <Form.Group widths='equal'>
                <Form.Field>
                  <Form.Input
                    label="City"
                    name="city"
                    value={formValues.city}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    maxLength={50}
                  />
                  {formValues.city?.length === 50 && <div className="error-message" style={{ color: 'red' }}>Maximum character limit exceeded</div>}
                </Form.Field>
                <Form.Field>
                  <div className='field'>
                    <label>State</label>
                    <Dropdown
                      name="stateId"
                      options={convertToDropDownProps(masterData?.state || [])}
                      fluid
                      selection
                      clearable
                      value={formValues.stateId ? formValues.stateId : ''}
                      onChange={handleDropdownChange}
                      onBlur={handleDropdownBlur}
                    />
                  </div>
                </Form.Field>
                <Form.Field>
                  <Form.Input
                    label="Zip"
                    name="zipCode"
                    value={formValues.zipCode}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    maxLength={10}
                  />
                </Form.Field>
              </Form.Group>
              <Form.Group widths='equal'>
                <Form.Field>
                  <div className='field'>
                    <label>Ward</label>
                    <Dropdown
                      name="wardId"
                      options={convertToDropDownProps(wards || [])}
                      fluid
                      selection
                      clearable
                      value={formValues.wardId ? formValues.wardId : ''}
                      onChange={handleDropdownChange}
                      onBlur={handleDropdownBlur}
                    />
                  </div>
                </Form.Field>
                <Form.Field>
                  <div className='field'>
                    <label>Neighborhood</label>
                    <Dropdown
                      name="neighborhoodId"
                      options={convertToDropDownProps(neighborhoods || [])}
                      fluid
                      selection
                      clearable
                      value={formValues.neighborhoodId ? formValues.neighborhoodId : ''}
                      onChange={handleDropdownChange}
                      onBlur={handleDropdownBlur}
                    />
                  </div>
                </Form.Field>
                <Form.Field className='cases'>
                  <a href="http://apps.ci.minneapolis.mn.us/AddressPortalApp/Search?AppID=PIApp" target="_blank" rel="noreferrer">Lookup<Icon name='external' className='no-u ml-2' /></a>
                </Form.Field>
              </Form.Group>
              <Form.Group widths='equal'>
                <Form.Field>
                  <Form.Input
                    label="Organization"
                    name="organization"
                    value={formValues.organization}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    maxLength={50}
                  />
                  {formValues.organization?.length === 50 && <div className="error-message" style={{ color: 'red' }}>Maximum character limit exceeded</div>}
                </Form.Field>
              </Form.Group>
              {constituentId > 0 && (
                <Form.Field>
                  <div className='field'>
                    <label>Created</label>
                    <span style={{ fontSize: '1.5rem' }}>{moment(formValues?.createdDate).format('MMM D, YYYY')} - {formValues.createdBy}</span>
                  </div>
                </Form.Field>
              )}
              {constituentId === 0 && (
                <div className='field'>
                  <label>Notes</label>
                  <TextArea
                    name="notes"
                    value={formValues.notes ? formValues.notes : ''}
                    onChange={handleTextAreaChange}
                    onBlur={handleBlur}
                    maxLength={3000}
                  />
                </div>
              )}
              <Grid style={{ marginBottom: '1rem' }}>
                <Grid.Column>
                  <Button type="submit" onClick={handleSubmit} className='violet right floated button' style={{ padding: '1.1rem 2.1rem', borderRadius: '0rem' }}>
                    {constituentId === 0 ? 'Create' : 'Update'}
                  </Button>
                  <Button type="button" className='second right floated button' style={{ padding: '1.1rem 2.1rem', borderRadius: '0rem', color: 'white' }} onClick={() => {
                    if (isModal === undefined) {
                      navigate('/constituents')
                    } else {
                      successCount = 0;
                      handleModalCancel(false);
                    }
                  }}>
                    Cancel
                  </Button>
                  {constituentId > 0 && formValues.caseCount === 0 &&
                    <Button type="button" className='danger button'
                      title="Delete"
                      style={{ padding: '1.1rem 2.1rem', borderRadius: '0rem', color: 'white' }}
                      onClick={handleDelete}>
                      <Icon name="trash alternate outline" style={{ marginLeft: '5px' }} />
                    </Button>
                  }
                </Grid.Column>
              </Grid>
            </Dimmer.Dimmable>
            {constituentId > 0 && (
              <Dimmer.Dimmable as="div" dimmed={isNoteLoading}>
                <Dimmer active={isNoteLoading} inverted={true}>
                  <Loader>Loading</Loader>
                </Dimmer>
                <div className='field'>
                  <label>Notes</label>
                  <TextArea
                    style={{ minHeight: 100 }}
                    name="notes"
                    value={formValues.notes ? formValues.notes : ''}
                    onChange={handleTextAreaChange}
                    onBlur={handleBlur}
                    maxLength={3000}
                    onFocus={() => setIsNoteFocus(true)}
                  />
                  {noteFormErrors?.notes && <span style={{ color: 'red' }}>{noteFormErrors?.notes}</span>}
                </div>
                {isNoteFocus && (
                  <Grid>
                    <Grid.Column>
                      <Button type="submit" onClick={handleNoteSubmit} className='violet right floated button' style={{ padding: '1.1rem 2.1rem', borderRadius: '0rem' }}>
                        Save
                      </Button>
                      <Button type="button" className='second right floated button' style={{ padding: '1.1rem 2.1rem', borderRadius: '0rem', color: 'white' }} onClick={() => {
                        setIsNoteFocus(false);
                        setFormValues({ ...formValues, notes: '' });
                      }}>
                        Cancel
                      </Button>
                    </Grid.Column>
                  </Grid>
                )}
                {constituentNoteResponse?.constituentNotes && (constituentNoteResponse?.constituentNotes || []).length > 0 && (
                  <Grid>
                    <h2 style={{ marginTop: 10, marginBottom: 10 }}>Note history</h2>
                    <Grid.Row>
                      <Grid.Column>
                        <List className='communication-history' divided>
                          {constituentNoteResponse?.constituentNotes?.map((constituentNote, noteIndex) => {
                            return (
                              <ConstituentNote key={Math.random()} constituentId={formValues.constituentId} constituentNote={constituentNote} noteIndex={noteIndex} />
                            )
                          })}
                        </List>
                      </Grid.Column>
                    </Grid.Row>
                  </Grid>
                )}
              </Dimmer.Dimmable>
            )}
          </Form>
        </Dimmer.Dimmable>
        <br />
        <br />
      </div >
    </section >
  );
}

export default ConstituentForm;